/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import styles from "./PuckThresholdsModal.module.css";
import { FloModal, FloModalBodyProps, FloMultipleSlider } from "components";
import { useTranslation } from "react-i18next";
import Message from "containers/common/Message";
import { Col, Row } from "react-bootstrap";
import classnames from "classnames";
import { Location, Device } from "model";
import ContentLoader from "react-content-loader";
import { ThresholdSettings } from "model/Threshold";
import Toggle from "react-toggle";
import "react-toggle/style.css";

type TagListProps = {
  list: string[];
  max: number;
};

enum Icon {
  TEMPERATURE,
  PRESSURE,
  HUMIDITY,
  FLOW,
  BATTERY_FULL,
  BATTERY_MED,
  BATTERY_EMPTY,
}

type TelemetryWidgetProps = {
  title: string;
  icon: Icon;
};

const PuckTelemetryWidget: React.FC<TelemetryWidgetProps> = React.memo(
  ({ title, icon }) => (
    <div className={styles.measureBox}>
      <div
        className={classnames(
          styles.measureIcon,
          { [styles.temperature]: icon === Icon.TEMPERATURE },
          { [styles.pressure]: icon === Icon.PRESSURE },
          { [styles.flow]: icon === Icon.FLOW },
          { [styles.humidity]: icon === Icon.HUMIDITY },
          { [styles.battery100]: icon === Icon.BATTERY_FULL },
          { [styles.battery50]: icon === Icon.BATTERY_MED },
          { [styles.battery10]: icon === Icon.BATTERY_EMPTY }
        )}
      ></div>
      <div className={styles.title}>{title}</div>
    </div>
  )
);

const TagList: React.FC<TagListProps> = React.memo(({ list, max = 6 }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.tagListRoot}>
      {list.slice(0, max).map((l, i) => (
        <div key={`tag-list=${i}`} className={styles.tagListElement}>
          {l}
        </div>
      ))}
      {list.length > max && (
        <div className={classnames(styles.tagListElement, styles.tagListMore)}>
          {t("set-thresholds-location-info-more", { count: list.length - max })}
        </div>
      )}
    </div>
  );
});

interface PuckThresholdsModalBodyProps extends FloModalBodyProps {
  locations: Location[];
  thresholds: ThresholdSettings;
  device: Device;
  onChange: (thresholds: ThresholdSettings) => void;
  onSave: (device: Device, settings: ThresholdSettings) => void;
}

const PuckThresholdsModalBody: React.FC<PuckThresholdsModalBodyProps> =
  React.memo(
    ({ locations, device, thresholds, onChange, onSave, bindSubmitForm }) => {
      const { t } = useTranslation();

      bindSubmitForm(async () => {
        onSave(device, thresholds);

        onChange(thresholds);
      });

      const [ humidityEnabled, setHumidityEnabled ] = useState(thresholds.humidityEnabled);
      const [ tempEnabled, setTempEnabled ] = useState(thresholds.tempEnabled);
      const [ batteryEnabled, setBatteryEnabled ] = useState(thresholds.batteryEnabled);

      return (
        <>
          <Row className={styles.bodyContainer}>
            <Col xs="12">
              <Row>
                <Col xs="12">
                  <Message />
                </Col>
              </Row>
              <Row>
                <Col xs="12">
                  <div>
                    <div
                      className={styles.locationInfo}
                      dangerouslySetInnerHTML={{
                        __html: t("set-thresholds-location-info-title", {
                          count: locations.length,
                        }),
                      }}
                    ></div>
                    <TagList
                      list={locations.map((l) => l.nickname || l.address || "")}
                      max={7}
                    />
                  </div>
                  <div className={styles.groupContainer}>
                    <div className={styles.sliderGroup}>
                      <div className={styles.container}>
                        <Col
                          xs={{ span: 4, offset: 0 }}
                          className={styles.smallerColPadding}
                        >
                          <PuckTelemetryWidget
                            title={t("Humidity")}
                            icon={Icon.HUMIDITY}
                          />
                        </Col>
                        <Col
                          xs={{ span: 4, offset: 6 }}
                          className={styles.smallerColPadding}
                        >
                          <Toggle
                            id="humidity-enabled"
                            icons={false}
                            disabled={false}
                            checked={humidityEnabled}
                            className={styles.toggle}
                            onChange={() => {
                              thresholds.humidityEnabled = !humidityEnabled;
                              setHumidityEnabled(!humidityEnabled);
                            }}
                          />
                        </Col>
                      </div>
                      <FloMultipleSlider
                        min={0}
                        max={100}
                        step={1}
                        minValue={thresholds.humidity.okMin}
                        maxValue={thresholds.humidity.okMax}
                        disabled={!humidityEnabled}
                        rightSymbol={"%"}
                        leftSymbol={"%"}
                        onChange={() => {}}
                        onMultipleChange={(values: readonly number[]) => {
                          let okMin = values[0];
                          let okMax = values[1];

                          onChange({
                            ...thresholds,
                            humidity: {
                              ...thresholds.humidity,
                              okMin,
                              okMax,
                            },
                          });
                        }}
                      />
                    </div>
                    <div className={styles.sliderGroup}>
                      <div className={styles.container}>
                        <Col
                          xs={{ span: 4, offset: 0 }}
                          className={styles.smallerColPadding}
                        >
                          <PuckTelemetryWidget
                            title={t("Temperature")}
                            icon={Icon.TEMPERATURE}
                          />
                        </Col>
                        <Col
                          xs={{ span: 4, offset: 6 }}
                          className={styles.smallerColPadding}
                        >
                          <Toggle
                            id="temp-enabled"
                            icons={false}
                            disabled={false}
                            checked={tempEnabled}
                            className={styles.toggle}
                            onChange={() => {
                              thresholds.tempEnabled = !tempEnabled;
                              setTempEnabled(!tempEnabled);                              
                            }}
                          />
                        </Col>
                      </div>
                      <FloMultipleSlider
                        min={0}
                        max={140}
                        step={1}
                        minValue={thresholds.tempF.okMin}
                        maxValue={thresholds.tempF.okMax}
                        rightSymbol={"ºF"}
                        leftSymbol={"ºF"}
                        disabled={!tempEnabled}
                        onChange={() => {}}
                        onMultipleChange={(values: readonly number[]) => {
                          let okMin = values[0];
                          let okMax = values[1];

                          onChange({
                            ...thresholds,
                            tempF: {
                              ...thresholds.tempF,
                              okMin,
                              okMax,
                            },
                          });
                        }}
                      />
                    </div>
                    <div className={styles.sliderGroup}>
                      <div className={styles.container}>
                        <Col
                          xs={{ span: 4, offset: 0 }}
                          className={styles.smallerColPadding}
                        >
                          <PuckTelemetryWidget
                            title={t("Low Battery Warning")}
                            icon={Icon.BATTERY_EMPTY}
                          />
                        </Col>
                        <Col xs={{ span: 4, offset: 6 }} className={styles.smallerColPadding}>
                          <Toggle
                            id="battery-enabled"
                            icons={false}
                            disabled={false}
                            checked={batteryEnabled}
                            className={styles.toggle}
                            onChange={() => {
                              thresholds.batteryEnabled = !batteryEnabled;
                              setBatteryEnabled(!batteryEnabled);
                            }}
                          />
                        </Col>
                      </div>
                      <FloMultipleSlider
                        min={15}
                        max={100}
                        step={1}
                        minValue={thresholds.battery.okMin}
                        maxValue={0}
                        rightSymbol={"%"}
                        leftSymbol={""}
                        disabled={!batteryEnabled}
                        onChange={(okMin: number) => {
                          onChange({
                            ...thresholds,
                            battery: {
                              ...thresholds.battery,
                              okMin,
                            },
                          });
                        }}
                        onMultipleChange={() => {}}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </>
      );
    }
  );

const ThresholdsSkeleton: React.FC<{}> = () => (
  <ContentLoader height={400} width={"100%"} speed={3}>
    <rect x="0" y="0" rx="3" ry="3" width="200" height="20" />

    <rect x="0" y="50" rx="3" ry="3" width="80" height="24" />
    <rect x="90" y="50" rx="3" ry="3" width="110" height="24" />
    <rect x="210" y="50" rx="3" ry="3" width="60" height="24" />
    <rect x="280" y="50" rx="3" ry="3" width="130" height="24" />

    <rect x="0" y="120" rx="3" ry="3" width="140" height="20" />
    <rect x="0" y="150" rx="3" ry="3" width={"100%"} height="30" />

    <rect x="0" y="210" rx="3" ry="3" width="140" height="20" />
    <rect x="0" y="240" rx="3" ry="3" width={"100%"} height="30" />
  </ContentLoader>
);

type ThresholdsModalProps = {
  show: boolean;
  onHide: () => void;
  onSave: () => void;
  isSubmitting: boolean;
  locations: Location[];
  device: Device;
  thresholds: ThresholdSettings | null;
  fetchThresholdSettings: (locations: Location[]) => void;
  updateThresholdsSettings: (settings: ThresholdSettings) => void;
  updateDevice: (device: Device, settings: ThresholdSettings) => void;
  clearMessage: () => void;
  hasError: boolean;
};

export const PuckThresholdsModal: React.FC<ThresholdsModalProps> = React.memo(
  ({
    show,
    locations,
    device,
    thresholds,
    fetchThresholdSettings,
    updateThresholdsSettings,
    updateDevice,
    clearMessage,
    hasError,
    ...rest
  }) => {
    const { t } = useTranslation();

    useEffect(() => {
      if (show) {
        clearMessage();
        fetchThresholdSettings(locations);
      }
    }, [show]);

    return (
      <FloModal
        {...rest}
        show={show}
        name="set-thresholds-modal"
        title={t("Set Puck Thresholds")}
        className={styles.root}
        disableButtons={thresholds === null}
        closeOnSubmit={true}
        body={(props) =>
          thresholds ? (
            <PuckThresholdsModalBody
              {...props}
              locations={locations}
              device={device}
              thresholds={thresholds}
              onChange={(thresholds) => updateThresholdsSettings(thresholds)}
              onSave={(device) => updateDevice(device, thresholds)}
            />
          ) : (
            <ThresholdsSkeleton />
          )
        }
      />
    );
  }
);
